import {TaskTemplateDTO} from "@energy-advice/easas-central-api";
import {AxiosInstance} from "axios";
import {APIPromise, easasAxios, requestPromiseTypeWrapper} from "services/HttpService";
import {
    AirflowDag,
    AirflowDagDto,
    AirflowTaskHistoriesPaginatedRequest,
    AirflowSearchQuery,
    AirflowSearchResult,
    AirflowTaskCreateResponse,
    AirflowTaskDto,
    AirflowUpdateDagResponse,
    PythonCalculationTask,
} from "types/airflow";
import {EASAS_REST_URL} from "utils/constants";
import {PythonCodeFileDTO} from "./model/airflow-task/PythonCodeFileDTO";
import {ScheduledTaskDTO} from "./model/airflow-task/ScheduledTaskDTO";
import {Instant} from "@js-joda/core";
import {addQueryParams} from "utils/http-utils";
import { PaginatedResponse } from "./model/dashboard/Dashboard";

const API_SCOPE = "/scheduled-tasks";
const API_SCOPE_AIRFLOW = "/airflow";
export const redirectToAirflowGraphUrl = (dagId: string) =>
  `${EASAS_REST_URL()}${API_SCOPE}/redirects/airflow-graph/${dagId}`;
export const redirectToAirflowHomeUrl = `${EASAS_REST_URL()}${API_SCOPE}/redirects/airflow-home`;

const promise = requestPromiseTypeWrapper;

class ScheduledTasksApiDefinition {
    private httpClient: AxiosInstance;

    constructor() {
        console.log("ScheduledTasksApi obj created!")
        this.httpClient = easasAxios;
    }

    async getAirflowTaskConfig(dagId: string, taskId: string): APIPromise<ScheduledTaskDTO> {
        try {
            const {data} = await this.httpClient.get(`${API_SCOPE}/airflow/${dagId}/${taskId}/config`);
            return {data: new ScheduledTaskDTO(data)}
        } catch (error: any) {
            console.error(error);
            return {error};
        }
    }

    async saveAirflowTaskConfig(config: ScheduledTaskDTO, run: boolean = false): APIPromise<ScheduledTaskDTO> {
        return promise(this.httpClient.post(`${API_SCOPE}/airflow/config`, config, {params: {run}}), (data) => new ScheduledTaskDTO(data));
    }

    async getAirflowTaskHistories(
      request: AirflowTaskHistoriesPaginatedRequest
    ): APIPromise<PaginatedResponse<IScheduledTaskHistoryDTO>> {
      return this.httpClient.get(
        `${API_SCOPE}/airflow/${request.dagId}/${request.taskId}/histories`,
        {
          params: { skip: request.skip, take: request.take },
        }
      );
    }

    async uploadCodeFile(file: File): APIPromise<PythonCodeFileDTO> {
        const formData = new FormData();
        formData.append('file', file);

        return promise(this.httpClient.post(`${API_SCOPE}/code/upload`, formData));
    }

    async getAirflowTaskDataInfos(dagId: string): APIPromise<IAirflowTaskInfo[]> {
        return promise<IAirflowTaskInfo[]>(this.httpClient.get(`${API_SCOPE_AIRFLOW}/dag/${dagId}/task-infos`));
    }

    async getCompanyDags(): APIPromise<AirflowDag[]> {
        return promise<AirflowDag[]>(this.httpClient.get(`${API_SCOPE}/dags`));
    }
  
    async getDag(dagId: string): APIPromise<AirflowDag> {
        return promise<AirflowDag>(this.httpClient.get(`${API_SCOPE}/dags/${dagId}`));
    }
  
    async createDag(dag: AirflowDagDto): APIPromise<AirflowDagDto> {
        return promise<AirflowDagDto>(this.httpClient.post(`${API_SCOPE}/dags`, dag));
    }
  
    async updateDag(dag: AirflowDagDto): APIPromise<AirflowUpdateDagResponse> {
        return promise<AirflowUpdateDagResponse>(this.httpClient.post(`${API_SCOPE}/dags/${dag.dagId}`, dag));
    }
  
    async deleteDag(dagId: string): APIPromise<AirflowDagDto> {
        return promise<AirflowDagDto>(this.httpClient.delete(`${API_SCOPE}/dags/${dagId}`));
    }

    async createTask(dagId: string, task: AirflowTaskDto): APIPromise<AirflowTaskCreateResponse> {
        return promise<AirflowTaskCreateResponse>(this.httpClient.post(`${API_SCOPE}/dags/${dagId}/tasks`, task));
    }

    async deleteTask(dagId: string, taskId: string): APIPromise<AirflowTaskDto> {
        return promise<AirflowTaskDto>(this.httpClient.delete(`${API_SCOPE}/dags/${dagId}/tasks/${taskId}`));
    }
  
    async getTasksUsage(usage: AirflowSearchQuery): APIPromise<AirflowSearchResult[]> {
        return promise<AirflowSearchResult[]>(this.httpClient.post(`${API_SCOPE}/search-usage`, usage));
    }

    async getTaskTemplates(): APIPromise<TaskTemplateDTO[]> {
        return promise<TaskTemplateDTO[]>(this.httpClient.get(`${API_SCOPE}/templates`));
    }

    async getPythonTaskTemplates(companyRef: string): APIPromise<PythonCalculationTask[]> {
        return promise<PythonCalculationTask[]>(this.httpClient.get(`${API_SCOPE}/python-templates/${companyRef}`));
    }

    getAirflowTaskJobDataLink(dagId: string, taskId: string, refTime?: Instant): string {
        return addQueryParams(`${EASAS_REST_URL()}${API_SCOPE}/airflow/${dagId}/${taskId}/test-data`, { referenceTime: refTime?.toString() })
    }

    async getAirflowTaskGraphLink(dagId: string, taskId: string): APIPromise<string> {
        return promise(this.httpClient.get(`${API_SCOPE_AIRFLOW}/dag/${dagId}/${taskId}/ti-log-link`));
    }
}

export interface IAirflowTaskInfo {
    dagId: string;
    taskId: string;
    dagTitle: string;
    taskTitle: string;
}

export interface IScheduledTaskHistoryDTO {
    id: string;
    label: string;
    taskDagId: string;
    taskId: string;
    oldId: string;
    isAirflow: boolean;
    config: string;
    changeBy: string;
    changeByCompanyRef: string;
    changeByCompanyTitle: string;
    changeOn: string;
}

export const ScheduledTasksApi = new ScheduledTasksApiDefinition()
